import { useState, useEffect, useMemo, useContext, useCallback } from 'react';
import AppContext from '../contexts/AppContext';
import { useNavigate } from 'react-router-dom';
import { fetchWithAuth } from '../utils/fetchWithAuth';
import { hasRole, ROLES } from '../utils/roleUtils';

export const useIssueColors = () => {
  const { serviceDesk } = useContext(AppContext);
  const serviceDeskConfig = serviceDesk?.config || {};

  const getPriorityColor = useCallback((priority) => {
    const colors = {
      high: 'bg-red-100 text-red-800',
      medium: 'bg-yellow-100 text-yellow-800',
      low: 'bg-green-100 text-green-800',
    };
    return colors[priority?.toLowerCase()] || 'bg-gray-100 text-gray-800';
  }, []);

  const getStatusColor = useCallback((status) => {
    const colors = {
      done: 'bg-green-100 text-green-800',
      closed: 'bg-green-100 text-green-800',
    };
    return colors[status?.toLowerCase()] || 'bg-gray-100 text-gray-800';
  }, []);

  const getRequestTypeColor = useCallback((requestType) => {
    const colors = serviceDeskConfig?.requestTypeSettings?.colors || {};
    return colors[requestType?.id] || '';
  }, [serviceDeskConfig]);

  return { getPriorityColor, getStatusColor, getRequestTypeColor };
};

export const useIssueNavigation = () => {
  const navigate = useNavigate();

  const handleNavigateToIssue = useCallback((issueKey, issue) => {
    navigate(`/issues/${issueKey}`, { state: { issue } });
  }, [navigate]);

  return { handleNavigateToIssue };
};

/** All requestTypeIds driven by a campaign (deployment, permanent maintenance…). */
const campaignRequestTypeIds = (serviceDeskConfig) =>
  Object.values(serviceDeskConfig?.campaigns ?? {})
    .map((c) => c?.requestTypeId)
    .filter(Boolean)
    .map(String);

/** True when an issue was created from a campaign (matched on its requestType, not its issueType). */
const isDeploymentLaunchIssue = (issue, serviceDeskConfig) => {
  const ids = campaignRequestTypeIds(serviceDeskConfig);
  if (!ids.length) return false;
  const issueRequestTypeId = issue?.mappedFields?.requestType?.requestType?.id;
  return ids.includes(String(issueRequestTypeId));
};

export const useIssuesData = ({ userRole, serviceDeskConfig, maxResults }) => {
  const [issuesOpen, setIssuesOpen] = useState([]);
  const [issuesPending, setIssuesPending] = useState([]);
  const [issuesDraft, setIssuesDraft] = useState([]);
  const [issuesClosed, setIssuesClosed] = useState(null);
  const [issuesToValidate, setIssuesToValidate] = useState([]);
  const [issuesArchived, setIssuesArchived] = useState(null);
  const [issuesDeployment, setIssuesDeployment] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);


  const fetchIssues = useCallback(async (closedFlag = false) => {
    try {
      setLoading(true);
      setError(null);
      const userName = localStorage.getItem('userName');
      const userId = localStorage.getItem('userId');

      if (!userName || !userId)
        return [];

      // Build query params
      const params = new URLSearchParams();
      if (closedFlag) params.append('closed', 'true');
      if (maxResults) params.append('maxResults', maxResults);
      const queryString = params.toString() ? `?${params.toString()}` : '';

      // Use different endpoint for technicians
      const endpoint = hasRole(userRole, ROLES.TECHNICIAN)
        ? `/issues/byTechnician${queryString}`
        : `/issues/bySectors${queryString}`;

      const response = await fetchWithAuth(endpoint);
      const responseData = await response.json();

      if (!response.ok) {
        throw new Error(responseData.error || 'Erreur lors de la récupération des tickets');
      }

      const allIssues = responseData.issues;

      if (!allIssues) {
        throw new Error('Erreur lors de la récupération des tickets');
      }

      // Campaign-launched tickets are kept in the regular list and also exposed via issuesDeployment for /calendar.
      const deploymentIssues = allIssues.filter(issue => isDeploymentLaunchIssue(issue, serviceDeskConfig));
      const issues = allIssues;

      if (!closedFlag) setIssuesDeployment(deploymentIssues);

      if (closedFlag) {
        const archivedStatusId = serviceDeskConfig?.issueValidation?.statusIdArchived;
        if (archivedStatusId) {
          setIssuesArchived(issues.filter(issue => parseInt(issue.fields.status.id) === archivedStatusId));
          setIssuesClosed(issues.filter(issue => parseInt(issue.fields.status.id) !== archivedStatusId));
        } else {
          setIssuesClosed(issues);
        }
        return;
      }

      if (serviceDeskConfig?.issueValidation?.enabled) {
        const brouillonStatusId = serviceDeskConfig?.issueValidation?.statusIdDraft;
        let remaining = issues;

        // Separate brouillon (draft) issues if configured
        if (brouillonStatusId) {
          setIssuesDraft(issues.filter(issue => parseInt(issue.fields.status.id) === brouillonStatusId));
          remaining = issues.filter(issue => parseInt(issue.fields.status.id) !== brouillonStatusId);
        }

        if (hasRole(userRole, 'manager-')) {
          setIssuesPending(remaining.filter(issue => parseInt(issue.fields.status.id) === serviceDeskConfig?.issueValidation?.statusIdOpen));
          setIssuesOpen(remaining.filter(issue => parseInt(issue.fields.status.id) !== serviceDeskConfig?.issueValidation?.statusIdOpen));
          return;
        }

        setIssuesToValidate(remaining.filter(issue => parseInt(issue.fields.status.id) === serviceDeskConfig?.issueValidation?.statusIdValidation));
        setIssuesOpen(remaining.filter(issue => parseInt(issue.fields.status.id) !== serviceDeskConfig?.issueValidation?.statusIdOpen && parseInt(issue.fields.status.id) !== serviceDeskConfig?.issueValidation?.statusIdValidation));
        return;

      }

      setIssuesOpen(issues);

    } catch (err) {
      console.error('Error fetching issues:', err);
      setError(err.message || 'Erreur inconnue');
    } finally {
      setLoading(false);
    }
  }, [userRole, serviceDeskConfig, maxResults]);

  useEffect(() => {
    if (!userRole) return;
    fetchIssues(false);
  }, [userRole]);

  const refetchOpen = useCallback(() => fetchIssues(false), [fetchIssues]);
  const refetchClosed = useCallback(() => fetchIssues(true), [fetchIssues]);
  const refetchArchived = useCallback(() => fetchIssues(true), [fetchIssues]);
  const refetchPending = useCallback(() => fetchIssues(false), [fetchIssues]);
  const refetchToValidate = useCallback(() => fetchIssues(false), [fetchIssues]);
  const refetchDraft = useCallback(() => fetchIssues(false), [fetchIssues]);
  return { issuesOpen, issuesPending, issuesDraft, issuesClosed, issuesToValidate, issuesArchived, issuesDeployment, loading, error, refetchOpen, refetchClosed, refetchArchived, refetchPending, refetchToValidate, refetchDraft };
};

export const useTableColumns = (getPriorityColor, getStatusColor, handleNavigateToIssue, { role }) => {

  return useMemo(() => [
    {
      key: 'key',
      label: 'Ticket',
      render: (item) => (
        <div
          className="text-sm font-medium text-primary cursor-pointer hover:text-primary/80 hover:underline"
          onClick={() => handleNavigateToIssue(item.key, item)}
        >
          {item.key}
        </div>
      ),
    },
    {
      key: 'summary',
      label: 'Titre',
      render: (item) => (
        <div>
          <div className="text-sm font-medium text-gray-900 max-w-[200px] truncate">
            {item.summary}
          </div>
          {item.updated && item.updated !== item.created && (
            <div className="text-xs text-gray-500">
              Mis à jour: {new Date(item.updated).toLocaleDateString('fr-FR')}
            </div>
          )}
        </div>
      ),
    },
    ...(role === 'admin' ? [
      {
        key: 'reporter',
        label: 'Reporter',
        render: (item) => (
          <span className="text-sm text-gray-900">{item.reporter}</span>
        ),
      },
    ] : []),
    {
      key: 'status',
      label: 'Statut',
      render: (item) => {
        const color = item?.statusCategory?.colorName || getStatusColor(item.status);

        return <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${color}`}>
          {item.status}
        </span>
      },
    },
    {
      key: 'priority',
      label: 'Priorité',
      render: (item) => (
        item.priority ? (
          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getPriorityColor(item.priority)}`}>
            {item.priority}
          </span>
        ) : (
          <span className="text-gray-400 text-sm">-</span>
        )
      ),
    },
    {
      key: 'requestType',
      label: 'Type',
      render: (item) => (
        item.requestType ? (
          <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-purple-100 text-purple-800">
            {item.requestType}
          </span>
        ) : (
          <span className="text-gray-400 text-sm">-</span>
        )
      ),
    },
    {
      key: 'created',
      label: 'Créé le',
      render: (item) => (
        <span className="text-sm text-gray-900">
          {item.created ? new Date(item.created).toLocaleDateString('fr-FR') : '-'}
        </span>
      ),
    },
    {
      key: 'resolution',
      label: 'Résolution',
      render: (item) => (
        item.resolution ? (
          <div>
            <div className="text-sm font-medium text-green-600">{item.resolution}</div>
            {item.resolutiondate && (
              <div className="text-xs text-gray-500">
                {new Date(item.resolutiondate).toLocaleDateString('fr-FR')}
              </div>
            )}
          </div>
        ) : (
          <span className="text-gray-400 text-sm">En cours</span>
        )
      ),
    },
  ], [getPriorityColor, getStatusColor, handleNavigateToIssue]);
};

/** Hook for loading and error state management. */
export const useLoadingStates = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const setLoadingState = useCallback((isLoading) => {
    setLoading(isLoading);
  }, []);

  const setErrorState = useCallback((errorMessage) => {
    setError(errorMessage);
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return { loading, error, setLoadingState, setErrorState, clearError };
};

/** Hook for loading and error UI components. */
export const useLoadingComponents = () => {
  const LoadingSpinner = () => (
    <div className="flex justify-center items-center min-h-screen">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
    </div>
  );

  const ErrorDisplay = ({ error, onRetry }) => (
    <div className="text-center py-12">
      <div className="max-w-md mx-auto">
        <svg className="mx-auto h-12 w-12 text-red-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.464 0L4.35 16.5c-.77.833.192 2.5 1.732 2.5z" />
        </svg>
        <h3 className="text-lg font-medium text-gray-900 mb-2">Erreur</h3>
        <p className="text-red-600 mb-4">{error}</p>
        {onRetry && (
          <button
            onClick={onRetry}
            className="bg-primary text-white px-4 py-2 rounded hover:bg-primary/90 transition-colors"
          >
            Réessayer
          </button>
        )}
      </div>
    </div>
  );

  const EmptyState = ({ message, description }) => (
    <div className="text-center py-12">
      <div className="max-w-md mx-auto">
        <svg className="mx-auto h-12 w-12 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
        <h3 className="text-lg font-medium text-gray-900 mb-2">{message}</h3>
        <p className="text-gray-500">{description}</p>
      </div>
    </div>
  );

  return { LoadingSpinner, ErrorDisplay, EmptyState };
};

/** Hook for issue details management. */
export const useIssueDetails = (issueKey) => {
  const [issue, setIssue] = useState(null);
  const [issueFromProps, setIssueFromProps] = useState(null);
  const [attachments, setAttachments] = useState([]);
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [attachmentsLoading, setAttachmentsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expandedAssets, setExpandedAssets] = useState({});
  const [selectedImage, setSelectedImage] = useState(null);
  const [expandedComments, setExpandedComments] = useState({});

  const toggleAsset = useCallback((fieldIndex, assetIndex) => {
    const key = `${fieldIndex}-${assetIndex}`;
    setExpandedAssets(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  }, []);

  const toggleComment = useCallback((commentIndex) => {
    setExpandedComments(prev => ({
      ...prev,
      [commentIndex]: !prev[commentIndex]
    }));
  }, []);

  return {
    issue,
    setIssue,
    issueFromProps,
    setIssueFromProps,
    attachments,
    setAttachments,
    comments,
    setComments,
    loading,
    setLoading,
    attachmentsLoading,
    setAttachmentsLoading,
    error,
    setError,
    expandedAssets,
    selectedImage,
    setSelectedImage,
    expandedComments,
    toggleAsset,
    toggleComment
  };
};

/** Hook for formatting utilities. */
export const useFormatters = () => {
  const formatDate = useCallback((dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('fr-FR');
  }, []);

  const formatDateTime = useCallback((dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('fr-FR');
  }, []);

  const formatFileSize = useCallback((bytes) => {
    if (!bytes) return 'Taille inconnue';
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    if (bytes === 0) return '0 Byte';
    const i = parseInt(Math.floor(Math.log(bytes) / Math.log(1024)));
    return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
  }, []);

  const truncateText = useCallback((text, maxLength = 50) => {
    if (!text) return '-';
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  }, []);

  return { formatDate, formatDateTime, formatFileSize, truncateText };
};
